import { useEffect, useState } from "react";
import { DeleteIcon, X } from "lucide-react";
import AnimatedSnackbar from "../../../STMPages/components/snackbars/AnimatedSnackbar";
import ConfimationPopup from "../../../STMPages/components/customPopup/ConfimationPopup";
import GlobalDeleteModal from "../../components/Modal/GlobalDeleteModal";
import {
  calculateFunds,
  PreviousFunds,
  addSponsor,
  deleteSponsor,
  addFundsPlan,
  deleteFundsPlan,
  fetchSponsorsDetails,
  fetchFundsPlanDetails,
  fetchSponsorsPersonelDetails,
  bankDetails,
} from "../../api/fundsCalculatorApi";
import { StepIndicator } from "./step-indicator";
import { FundsSummary } from "./funds-summary";
import { UniversityInfo } from "./university-info";
import { Step1Form } from "./step1-form";
import { Step2Form } from "./step2-form";
import { Step3Form } from "./step3-form";
import { ExpensesTable } from "./expenses-table";
import { SponsorsTable } from "./sponsors-table";
import { FundsPlanTable } from "./funds-plan-table";
import { NavigationFooter } from "./NavigationFooter";
import { ActionButtons } from "./ActionButtons";

const FundsCalculatorModal = ({ isOpen, onClose, currentStep = 1, course }) => {
  const UserId = localStorage.getItem("userId");
  const [openModal, setOpenModal] = useState(false);
  const [step, setStep] = useState(currentStep);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingFundsIndex, setEditingFundsIndex] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [edit, setEdit] = useState(false);
  const initialFormData = {
    maritalStatus: "",
    spouseAccompany: "",
    takingKids: "",
    childrenCount: 1,
    childrenAges: [""],
    sponsorName: "",
    relationship: "",
    occupation: 1,
    filledItr: 0,
    sourceOfIncome: 1,
    sponsorsAmount: "",
    SelectSponsorName: "",
    financialInstitutionName: 1,
    financialInstitutionCountry: 1,
    typeOfFunds: null,
    proofOfFunds: null,
    oldFunds: 1,
    sponsors: [],
    fundsPlans: [],
    sponsor_id: null,
    fund_cal_id: null,
  };
  const [formData, setFormData] = useState(initialFormData);
  const [fundsData, setFundsData] = useState({
    breakdown: [],
    total_amount: "0.00",
    total_amount_inr: "0.00",
    currency: "",
  });
  const [loading, setLoading] = useState(false);
  const [disableActions, setDisableActions] = useState(false);

  // const [fund_cal_id, setFund_cal_id] = useState(null);
  // const [sponsor_id, setSponsor_id] = useState(null);

  const fetchPreviousFunds = async (course) => {
    setLoading(true);
    try {
      const res = await PreviousFunds({
        user_id: UserId,
        inst_course_id: course?.id || "",
      });

      const response = await res.json();

      if (response.success) {
        setFundsData({
          last_calculated_date: response.data.last_calculated_date || "",
          fund_id: response.data.fund_id || "",
          breakdown: response.data.breakdown || [],
          total_amount: response.data.total_amount || "0.00",
          total_amount_inr: response.data.total_amount_inr || "0.00",
          currency: response.data.currency || "",
        });
      } else {
        setFundsData({
          breakdown: [],
          total_amount: "0.00",
          total_amount_inr: "0.00",
          currency: "",
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSponsors = async () => {
    try {
      setLoading(true);

      const res = await fetchSponsorsDetails({
        fund_cal_id: fundsData.fund_id,
      });

      const response = await res.json();

      if (response?.success && Array.isArray(response.data)) {
        const mappedSponsors = response.data.map((s) => ({
          id: s.id,
          sponsor_id: s.id,
          fund_cal_id: s.fund_cal_id,

          // Display values
          sponsorName: s.sponser_name?.trim() || "-",
          relationship: s.relations || "-", // e.g. "Mother"
          occupation: s.occupation_name || "-", // e.g. "Actor /Actress"
          sourceOfIncome: s.income_source_name || "-", // e.g. "Pensioner"
          filledItr: s.year_itr ?? "-", // e.g. 0 or 1

          // ID values (for dropdowns and editing)
          relationshipId: s.relation_with_sponser || null, // e.g. 2
          occupationId: s.sponser_occupation || null, // e.g. 2
          sourceOfIncomeId: s.source_of_income || null, // e.g. 3
        }));

        setFormData((prev) => ({
          ...prev,
          sponsors: mappedSponsors,
        }));
      } else {
        setFormData((prev) => ({ ...prev, sponsors: [] }));
      }
    } catch (err) {
      console.error("Error fetching sponsors:", err);
      setFormData((prev) => ({ ...prev, sponsors: [] }));
    } finally {
      setLoading(false);
    }
  };

  const fetchFundsPlan = async () => {
    try {
      setLoading(true);

      const res = await fetchFundsPlanDetails({
        fund_cal_id: fundsData.fund_id,
      });

      const response = await res.json();

      if (response?.success && Array.isArray(response.data)) {
        const mappedPlans = response.data.map((plan) => ({
          id: plan.id,
          sponsorsAmount: plan.sponser_amount || 0,
          financialInstitutionName: plan.bank_name?.trim() || "-",
          financialInstitutionCountry: plan.bank_country_name || "-",
          financialInstitutionCountryId: plan.bank_country_id || null,
          typeOfFunds: plan.fund_name || "-",
          proofOfFunds: plan.fund_doc_path || null,
          oldFunds: plan.six_months_old_path || null,
          sponsorName: plan.sponser_name || "N/A",
          funds_planing_id: plan.id || null,
        }));

        setFormData((prev) => ({
          ...prev,
          fundsPlans: mappedPlans,
        }));
      } else {
        setFormData((prev) => ({ ...prev, fundsPlans: [] }));
      }
    } catch (error) {
      console.error("Error fetching funds plan:", error);
      setFormData((prev) => ({ ...prev, fundsPlans: [] }));
    } finally {
      setLoading(false);
    }
  };

  const SponsorsPersonelDetails = async (relationId) => {
    try {
      const res = await fetchSponsorsPersonelDetails({
        fund_cal_id: fundsData.fund_id,
        relation_id: Number.parseInt(relationId || 1),
      });

      const response = await res.json();

      if (response?.success && response.data) {
        const data = response.data;

        setFormData((prev) => ({
          ...prev,
          sponsorName: data.sponser_name || "",
          relationship:
            data.relation_with_sponser?.toString() || relationId.toString(),
          occupation: data.sponser_occupation?.toString() || "",
          sourceOfIncome: data.source_of_income?.toString() || "",
          filledItr: data.year_itr?.toString() || "0",
        }));

        //  Disable buttons since autofill worked
        setDisableActions(true);
      } else {
        showSnackbar(
          "Sponsor details not found, please enter details manually",
          "error",
        );
        setDisableActions(false); // re-enable if no data found
      }
    } catch (err) {
      console.error("Error fetching sponsors:", err);
      showSnackbar("Error fetching sponsor details", "error");
      setDisableActions(false);
    }
  };

  useEffect(() => {
    fetchPreviousFunds(course);
    if (step === 2) {
      fetchSponsors();
    } else if (step === 3) {
      fetchFundsPlan();
    }
  }, [course, step]);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleInputChange = async (field, value, index = null) => {
    if (field === "childrenCount") {
      const count = Number(value);
      setFormData((prev) => ({
        ...prev,
        childrenCount: count,
        childrenAges: Array(count)
          .fill("")
          .map((_, i) => prev.childrenAges[i] || ""),
      }));
      return;
    }

    if (field === "childrenAges" && index !== null) {
      setFormData((prev) => {
        const updatedAges = [...prev.childrenAges];
        updatedAges[index] = value;
        return { ...prev, childrenAges: updatedAges };
      });
      return;
    }

    //  Relationship handling
    if (field === "relationship") {
      //  Reset dependent sponsor fields before fetching
      setFormData((prev) => ({
        ...prev,
        relationship: value,
        sponsorName: "",
        occupation: "",
        sourceOfIncome: "",
        filledItr: "",
      }));

      //  Fetch and prefill new sponsor details if available
      await SponsorsPersonelDetails(value);
      return;
    }

    // All other fields
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  //STEP-1
  const HandleCalculateFunds = async () => {
    if (!validateStep()) return;
    try {
      setLoading(true);
      if (!course?.course_id || !UserId) {
        setSnackbarMessage("Missing course or user information!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const payload = {
        marital_status: Number(formData.maritalStatus),
        inst_course_id: Number(course.id),
        user_id: Number(UserId),
        calculator_spouse_accompany: formData.spouseAccompany === "Yes" ? 1 : 0,
        calculator_child_count:
          formData.takingKids === "Yes" ? Number(formData.childrenCount) : 0,
        calculator_child_age_1: formData.childrenAges[0]
          ? Number(formData.childrenAges[0])
          : 0,
        calculator_child_age_2: formData.childrenAges[1]
          ? Number(formData.childrenAges[1])
          : 0,
        calculator_child_age_3: formData.childrenAges[2]
          ? Number(formData.childrenAges[2])
          : 0,
        calculator_child_age_4: formData.childrenAges[3]
          ? Number(formData.childrenAges[3])
          : 0,
      };

      const res = await calculateFunds(payload);
      const response = await res.json();

      if (response.success) {
        const newData = response.data;

        setFundsData({
          breakdown: newData.breakdown || [],
          total_amount: newData.total_amount || "0.00",
          total_amount_inr: newData.total_amount_inr || "0.00",
          last_calculated_date: newData.last_calculated_date || "",
          currency: newData.currency || "",
        });

        setFormData({
          ...initialFormData,
          maritalStatus: formData.maritalStatus,
          isSingle: true,
        });

        setSnackbarMessage("Funds calculated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(response.data?.message || "Calculation failed.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error calculating funds:", error);
      setSnackbarMessage("Something went wrong while calculating funds.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  //STEP-2
  const handleAddSponsor = async () => {
    if (!validateStep()) return;

    if (!formData.sponsorName) {
      showSnackbar("Please enter sponsor name", "error");
      return;
    }

    try {
      setLoading(true);
      const fundId = fundsData.fund_id;

      const payload = {
        name_of_sponser: formData.sponsorName,
        relation_sponser: Number.parseInt(formData.relationship || 1),
        occupation_sponser: Number.parseInt(formData.occupation || 1),
        source_of_income: Number.parseInt(formData.sourceOfIncome || 1),
        year_itr: Number.parseInt(formData.filledItr) || 0,
        user_id: Number.parseInt(UserId),
        fund_id: Number.parseInt(fundId),
      };

      if (formData.SelectSponsorId) {
        payload.sponser_id = formData.SelectSponsorId;
      }

      const response = await addSponsor(payload);
      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbarMessage("Sponsor added successfully!");
        // setFund_cal_id(data.data.fund_cal_id || null);
        // setSponsor_id(data.data.id || null);
        setFormData((prev) => {
          const sponsors = [...prev.sponsors];

          const newSponsor = {
            sponsorName: formData.sponsorName,
            relationship: formData.relationship,
            occupation: formData.occupation,
            sourceOfIncome: formData.sourceOfIncome,
            filledItr: formData.filledItr,
            sponser_id: data.data.id,
          };

          if (editingIndex !== null) {
            sponsors[editingIndex] = newSponsor;
            setSnackbarMessage("Sponsor edited successfully!");
          } else {
            sponsors.push(newSponsor);
          }

          return {
            ...prev,
            sponsors,
            sponsorName: "",
            relationship: "",
            occupation: "",
            sourceOfIncome: "",
            filledItr: "",
          };
        });

        setEditingIndex(null);
        setEdit(false);
        showSnackbar(
          editingIndex !== null
            ? "Sponsor updated successfully"
            : "Sponsor added successfully",
          "success",
        );
        fetchSponsors();
      } else {
        showSnackbar(data.message || "Failed to save sponsor", "error");
      }
    } catch (error) {
      console.error("Add Sponsor Error:", error);
      showSnackbar("Something went wrong while saving sponsor", "error");
    } finally {
      setLoading(false);
    }
  };

  //STEP-3
  const handleAddFundsPlan = async () => {
    if (!validateStep()) return;

    const newPlanAmount = Number.parseFloat(formData.sponsorsAmount) || 0;

    const totalWithoutEditing =
      totalSponsorAmount -
      (editingFundsIndex !== null
        ? Number.parseFloat(
            formData.fundsPlans[editingFundsIndex]?.sponsorsAmount,
          ) || 0
        : 0);

    const newTotalSponsorAmount = totalWithoutEditing + newPlanAmount;

    if (newTotalSponsorAmount > totalPlannedAmount) {
      showSnackbar(
        "Total sponsor amount cannot exceed total planned amount.",
        "error",
      );
      return;
    }

    try {
      setLoading(true);
      const fundId = fundsData.fund_id;

      const payload = new FormData();
      payload.append("user_id", UserId);
      payload.append("sponser_amount", formData.sponsorsAmount);
      payload.append("bank_id", formData.financialInstitutionName);
      payload.append("bank_country_id", formData.financialInstitutionCountry);
      payload.append("fund_type_id", formData.typeOfFunds);
      // Find selected sponsor
      const selectedSponsor = formData.sponsors.find(
        (s) => s.sponsorName === formData.SelectSponsorName,
      );

      payload.append("fund_cal_id", selectedSponsor?.fund_cal_id ?? "");
      payload.append("sponser_id", selectedSponsor?.sponsor_id ?? "");

      payload.append("six_months_old_path", formData.oldFunds);

      if (formData.proofOfFunds)
        payload.append("fund_doc_path", formData.proofOfFunds);

      if (formData.fundsPlans[editingFundsIndex]?.id) {
        payload.append(
          "fund_planing_id",
          formData.fundsPlans[editingFundsIndex].id,
        );
      }

      const response = await addFundsPlan(payload);
      const data = await response.json();

      if (response.ok && data.success) {
        setFormData((prev) => {
          const fundsPlans = [...prev.fundsPlans];

          const newPlan = {
            SelectSponsorName: prev.SelectSponsorName,
            SelectSponsorId: prev.SelectSponsorId,
            sponsorsAmount: prev.sponsorsAmount,
            financialInstitutionName: prev.financialInstitutionName,
            financialInstitutionNameId: prev.financialInstitutionNameId,
            financialInstitutionCountry: prev.financialInstitutionCountry,
            financialInstitutionCountryId: prev.financialInstitutionCountryId,
            typeOfFunds: prev.typeOfFunds,
            proofOfFunds: prev.proofOfFunds,
            oldFunds: prev.oldFunds,
            fund_planing_id: prev.id,
          };

          if (editingFundsIndex !== null) {
            fundsPlans[editingFundsIndex] = newPlan;
          } else {
            fundsPlans.push(newPlan);
          }

          return {
            ...prev,
            fundsPlans,
            SelectSponsorName: "",
            SelectSponsorId: "",
            sponsorsAmount: "",
            financialInstitutionName: "",
            financialInstitutionNameId: "",
            financialInstitutionCountry: "",
            financialInstitutionCountryId: "",
            typeOfFunds: "",
            proofOfFunds: null,
            oldFunds: "",
            fund_planing_id: null,
          };
        });

        setEditingFundsIndex(null);
        setEdit(false);

        showSnackbar(
          editingFundsIndex !== null
            ? "Funds plan updated successfully"
            : "Funds plan added successfully",
          "success",
        );
        fetchFundsPlan();
      } else {
        showSnackbar(data.message || "Failed to save funds plan", "error");
      }
    } catch (err) {
      console.error("Add Funds Plan Error:", err);
      showSnackbar("Something went wrong while saving funds plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSponsor = (index) => {
    try {
      setLoading(true);
      const sponsor = formData.sponsors[index];
      if (!sponsor) return;

      setEdit(true);
      setEditingIndex(index);

      setFormData((prev) => ({
        ...prev,
        sponsorName: sponsor.sponsorName || "",
        SelectSponsorId: sponsor.sponsor_id || "",

        // Use IDs for dropdowns (preferred for selects)
        relationship: sponsor.relationshipId || "",
        occupation: sponsor.occupationId || "",
        sourceOfIncome: sponsor.sourceOfIncomeId || "",
        filledItr: sponsor.filledItr ?? "",

        // reset file inputs if any
        proofOfFunds: null,
      }));
    } catch (err) {
      console.error("Error editing sponsor:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSponsor = async (index) => {
    try {
      setLoading(true);

      const sponsorToDelete = formData.sponsors[index];

      if (sponsorToDelete?.id) {
        const payload = { sponser_id: sponsorToDelete.id };

        const response = await deleteSponsor(payload);

        const data =
          typeof response.json === "function"
            ? await response.json()
            : response;

        if (response.ok || data?.success) {
          setFormData((prev) => ({
            ...prev,
            sponsors: prev.sponsors.filter((_, i) => i !== index),
          }));

          showSnackbar("Sponsor deleted successfully", "success");
        } else {
          showSnackbar(data?.message || "Failed to delete sponsor", "error");
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          sponsors: prev.sponsors.filter((_, i) => i !== index),
        }));

        showSnackbar("Sponsor removed locally", "info");
      }
    } catch (error) {
      console.error("Delete Sponsor Error:", error);
      showSnackbar("Something went wrong while deleting sponsor", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleEditFundsPlan = async (index) => {
    try {
      setLoading(true);
      setEdit(true);

      const plan = formData.fundsPlans[index];

      // Step 1: Prefill country first
      setFormData((prev) => ({
        ...prev,
        SelectSponsorName: plan.sponsorName || "",
        SelectSponsorId: plan.sponser_id || "",
        sponsorsAmount: plan.sponsorsAmount || "",
        financialInstitutionCountry:
          plan.financialInstitutionCountryId?.toString() || "",
        financialInstitutionName: "", // temp blank until banks load
        typeOfFunds: plan.typeOfFunds || "",
        oldFunds: plan.oldFunds ? 1 : 0,
        proofOfFunds: null,
        fund_planing_id: plan.funds_planing_id || null,
      }));

      // Step 2: Wait a tick so country change triggers bank fetch
      setTimeout(async () => {
        const response = await bankDetails({
          country_id: plan.financialInstitutionCountryId,
        });
        const banks = response?.data?.data || [];
        const matchedBank = banks.find(
          (b) => b.bank_name === plan.financialInstitutionName,
        );

        if (matchedBank) {
          setFormData((prev) => ({
            ...prev,
            financialInstitutionName: matchedBank.id.toString(),
          }));
        }
      }, 500);

      setEditingFundsIndex(index);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFundsPlan = async (index) => {
    try {
      setLoading(true);

      const selectedPlan = formData.fundsPlans[index];

      if (selectedPlan?.id) {
        const payload = { fund_planing_id: selectedPlan.id };

        const response = await deleteFundsPlan(payload);
        const data =
          typeof response.json === "function"
            ? await response.json()
            : response;

        if (response.ok || data?.success) {
          setFormData((prev) => ({
            ...prev,
            fundsPlans: prev.fundsPlans.filter((_, i) => i !== index),
          }));

          showSnackbar("Funds plan deleted successfully", "success");
        } else {
          showSnackbar(data?.message || "Failed to delete funds plan", "error");
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          fundsPlans: prev.fundsPlans.filter((_, i) => i !== index),
        }));

        showSnackbar("Funds plan removed locally", "info");
      }
    } catch (err) {
      console.error("Delete Funds Plan Error:", err);
      showSnackbar("Something went wrong while deleting funds plan", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const steps = [
    { id: 1, title: "Funds calculator" },
    { id: 2, title: "Sponsor Details" },
    { id: 3, title: "Financial Planning" },
  ];

  const universityData = {
    flag: course?.country_code || "us",
    countryName: course?.country_name || "Unknown",
    name: course?.university_name || "",
    courseName: course?.course_name || "",
    duration: course?.duration || "",
    annualFees: course?.annual_tution_fees || "0",
    totalFees: course?.total_tution_fees || "",
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.maritalStatus) {
        showSnackbar("Please select marital status", "error");
        return false;
      }

      if (formData.isSpouseRequired && !formData.spouseAccompany) {
        showSnackbar(
          "Please select if your spouse will accompany you",
          "error",
        );
        return false;
      }

      if (formData.isKidsAllowed) {
        if (!formData.takingKids) {
          showSnackbar("Please select if you are taking kids", "error");
          return false;
        }

        if (formData.takingKids === "Yes") {
          if (!formData.childrenCount || formData.childrenCount <= 0) {
            showSnackbar("Please select the number of children", "error");
            return false;
          }

          for (let i = 0; i < formData.childrenCount; i++) {
            if (!formData.childrenAges[i]) {
              showSnackbar(
                `Please enter a valid age for child ${i + 1}`,
                "error",
              );
              return false;
            }
          }
        }
      }

      return true;
    }

    if (step === 2) {
      if (!formData.sponsorName) {
        showSnackbar("Please enter sponsor name", "error");
        return false;
      }
      if (!formData.relationship) {
        showSnackbar("Please select relationship", "error");
        return false;
      }
      if (!formData.occupation) {
        showSnackbar("Please select occupation", "error");
        return false;
      }
      if (!formData.sourceOfIncome) {
        showSnackbar("Please select source of income", "error");
        return false;
      }
      // if (!formData.filledItr) {
      //   showSnackbar("Please select if ITR is filed", "error");
      //   return false;
      // }
    }

    if (step === 3) {
      if (!formData.SelectSponsorName) {
        showSnackbar("Please select a sponsor", "error");
        return false;
      }
      if (
        !formData.sponsorsAmount ||
        isNaN(formData.sponsorsAmount) ||
        Number(formData.sponsorsAmount) <= 0
      ) {
        showSnackbar("Please enter a valid sponsor amount", "error");
        return false;
      }
      if (!formData.financialInstitutionName) {
        showSnackbar("Please select financial institution name", "error");
        return false;
      }
      if (!formData.financialInstitutionCountry) {
        showSnackbar("Please select financial institution country", "error");
        return false;
      }
      if (!formData.typeOfFunds) {
        showSnackbar("Please select type of funds", "error");
        return false;
      }
      if (formData.oldFunds !== 0 && formData.oldFunds !== 1) {
        showSnackbar("Please select if funds are 6 months old", "error");
        return false;
      }
    }

    return true;
  };

  const isContinueDisabled = () => {
    // if (step === 1) {
    //   return fundsData.breakdown.length === 0;
    // }
    if (step === 2) {
      return formData.sponsors.length === 0;
    }
    if (step === 3) {
      return formData.fundsPlans.length < formData.sponsors.length;
    }
    return false;
  };

  const handleContinue = () => {
    if (isContinueDisabled()) {
      if (step === 1) {
        showSnackbar("Please calculate fund before continuing.", "error");
      } else if (step === 2) {
        showSnackbar("Add at least one sponsor to continue.", "error");
      } else if (step === 3) {
        showSnackbar("Add a fund plan for each sponsor to continue.", "error");
      }
      return;
    }

    setStep(step + 1);
  };

  const totalPlannedAmount = fundsData.total_amount_inr;

  const totalSponsorAmount = formData.fundsPlans.reduce(
    (sum, sponsor) => sum + (Number.parseFloat(sponsor.sponsorsAmount) || 0),
    0,
  );

  const balanceAmount =
    totalPlannedAmount -
    formData.fundsPlans.reduce(
      (sum, sponsor) => sum + (Number.parseFloat(sponsor.sponsorsAmount) || 0),
      0,
    );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1100] bg-black/5">
      {openModal && (
        <ConfimationPopup
          handleClose={() => setOpenModal(false)}
          open={openModal}
          component={
            <GlobalDeleteModal
              text="Are you sure?"
              subText="This will remove the selected item."
              buttonTitle="Yes, Delete"
              startIcon={<DeleteIcon />}
              onClose={() => {
                setOpenModal(false);
                setDeletingIndex(null);
              }}
              onConfirm={() => {
                if (step === 2) {
                  handleDeleteSponsor(deletingIndex);
                } else if (step === 3) {
                  handleDeleteFundsPlan(deletingIndex);
                }

                setOpenModal(false);
                setDeletingIndex(null);
              }}
            />
          }
        />
      )}
      <AnimatedSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Header with close button */}
        <div className="sticky top-0 z-10 flex justify-end p-4 bg-white rounded-t-2xl">
          <button
            onClick={() => {
              onClose();
              setStep(1);
            }}
            className="text-gray-500 hover:text-gray-700 text-4xl font-bold"
          >
            <X />
          </button>
        </div>
        <div className="px-8 pb-10">
          {/* Step Indicator */}
          <StepIndicator steps={steps} currentStep={step} />

          {/* Funds Summary */}
          {(step === 2 || step === 3) && <FundsSummary fundsData={fundsData} />}

          {/* University Information */}
          {step === 1 && <UniversityInfo universityData={universityData} />}

          {/* Form Section */}
          {step === 1 && (
            <Step1Form
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
            />
          )}

          {step === 2 && (
            <Step2Form
              formData={formData}
              handleInputChange={handleInputChange}
              disabled={disableActions}
            />
          )}

          {step === 3 && (
            <Step3Form
              formData={formData}
              handleInputChange={handleInputChange}
            />
          )}

          <ActionButtons
            step={step}
            edit={edit}
            onCancel={() => {
              setEdit(false);
              setEditingIndex(null);
              setEditingFundsIndex(null);
              setDisableActions(false); //  re-enable after cancel
              setFormData((prev) => {
                if (step === 2) {
                  return {
                    ...prev,
                    sponsorName: "",
                    relationship: "",
                    occupation: "",
                    sourceOfIncome: "",
                    filledItr: "",
                  };
                } else if (step === 3) {
                  return {
                    ...prev,
                    SelectSponsorName: "",
                    SelectSponsorId: "",
                    sponsorsAmount: "",
                    financialInstitutionName: "",
                    financialInstitutionNameId: "",
                    financialInstitutionCountry: "",
                    financialInstitutionCountryId: "",
                    typeOfFunds: "",
                    proofOfFunds: null,
                    oldFunds: "",
                  };
                } else {
                  return {
                    ...prev,
                    maritalStatus: "",
                    spouseAccompany: "",
                    takingKids: "",
                    childrenCount: 1,
                    childrenAges: [""],
                  };
                }
              });
            }}
            onSubmit={() =>
              step === 1
                ? HandleCalculateFunds()
                : step === 2
                  ? handleAddSponsor()
                  : handleAddFundsPlan()
            }
            disabled={disableActions}
          />

          {/* Last Calculated Date */}
          {!loading && step === 1 && (
            <div className="font-altis flex justify-between items-center mb-6 bg-[#F5F5F5] p-3 rounded-lg">
              <span className="text-[#572575] text-[16px]">
                Last Calculated Date
              </span>
              <span className="text-black text-[16px] font-semibold">
                {fundsData.last_calculated_date
                  ? new Date(fundsData.last_calculated_date).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )
                  : ""}
              </span>
            </div>
          )}

          {/* Expenses Table */}
          {step === 1 && (
            <ExpensesTable
              // HandleCalculateFunds={HandleCalculateFunds}
              fundsData={fundsData}
              loading={loading}
              course={course}
            />
          )}

          {/* Sponsors Table */}
          {step === 2 && (
            <SponsorsTable
              sponsors={formData.sponsors}
              onEdit={handleEditSponsor}
              onDelete={(index) => {
                setDeletingIndex(index);
                setOpenModal(true);
              }}
              loading={loading}
            />
          )}

          {/* FundsPlan Table */}
          {step === 3 && (
            <FundsPlanTable
              fundsPlans={formData.fundsPlans}
              onEdit={handleEditFundsPlan}
              onDelete={(index) => {
                setDeletingIndex(index);
                setOpenModal(true);
              }}
            />
          )}

          {/* Modal Footer */}
          <NavigationFooter
            step={step}
            steps={steps}
            onBack={() => setStep(step - 1)}
            onContinue={handleContinue}
            isContinueDisabled={isContinueDisabled}
            totalPlannedAmount={totalPlannedAmount}
            balanceAmount={balanceAmount}
          />
        </div>
      </div>
    </div>
  );
};

export default FundsCalculatorModal;
